
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './ProjectStyle.css';
import { IoAdd, IoClose, IoPencil, IoInformationCircleOutline, IoSearch, IoGrid, IoList, IoAttach, IoCloudUploadOutline, IoTrashOutline, IoChevronDown, IoSaveOutline } from "react-icons/io5";

// --- Permission constants ---
const PERMISSIONS = {
    CREATE_PROJECT: 'CREATE PROJECT',
    VIEW_ALL_PROJECTS: 'VIEW PROJECT',
};

// --- Environment Variables ---
const SWAGGER_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;
const NODE_BASE_URL = process.env.REACT_APP_NODE_SERVICE_BASE_URL;
const ATTACHMENT_BASE_URL = process.env.REACT_APP_ATTACHMENT_SERVICE_BASE_URL || NODE_BASE_URL;

// --- Helper Functions ---
const formatDateTime = (isoString, type = 'datetime') => {
    if (!isoString) return 'N/A';
    try {
        const date = new Date(isoString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();

        if (type === 'date') return `${day} ${month} ${year}`;

        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day} ${month} ${year}   ${hours} : ${minutes}`;
    } catch (error) {
        console.error("Could not format date:", isoString, error);
        return 'Invalid Date';
    }
};

// --- Remark Helper Functions ---
const formatNewRemark = (newRemarkText, userName, existingRemarksData) => {
    let existingRemarksString = '';
    if (Array.isArray(existingRemarksData)) {
        existingRemarksString = existingRemarksData.filter(r => typeof r === 'string' && r.trim()).join('\n---\n');
    } else if (typeof existingRemarksData === 'string') {
        existingRemarksString = existingRemarksData;
    }

    if (!newRemarkText || !newRemarkText.trim()) {
        return existingRemarksString;
    }

    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = now.toLocaleString('default', { month: 'short' });
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    
    const timestamp = `[${day} ${month} ${year} ${hours}:${minutes}]`;
    const newEntry = `${timestamp} (${userName}): ${newRemarkText.trim()}`;
    
    return existingRemarksString ? `${newEntry}\n---\n${existingRemarksString}` : newEntry;
};

const parseRemarks = (remarksData) => {
    let remarksString = '';

    if (Array.isArray(remarksData)) {
        remarksString = remarksData.filter(r => typeof r === 'string' && r.trim()).join('\n---\n');
    } else if (typeof remarksData === 'string') {
        remarksString = remarksData;
    } else {
        return [];
    }

    if (!remarksString.trim()) {
        return [];
    }
    
    return remarksString.split('\n---\n').map(entry => {
        const match = entry.match(/\[(.*?)\] \((.*?)\): (.*)/s);
        if (match) {
            return { timestamp: match[1]?.trim(), user: match[2]?.trim(), text: match[3]?.trim() };
        }
        return { timestamp: 'Previously', user: 'System', text: entry.trim() };
    }).filter(remark => remark.text);
};


// --- Hierarchy Helpers ---
const findUserInHierarchy = (tree, userName) => {
    for (const node of tree) {
        if (node.name === userName) return node;
        if (node.children && node.children.length > 0) {
            const found = findUserInHierarchy(node.children, userName);
            if (found) return found;
        }
    }
    return null;
};

const getAllSubordinates = (userNode) => {
    let subordinates = [];
    if (userNode?.children?.length) {
        for (const child of userNode.children) {
            if (child.id && child.name) {
                subordinates.push({ id: child.id, name: child.name });
            }
            subordinates = subordinates.concat(getAllSubordinates(child));
        }
    }
    return subordinates;
};

const filterActiveHierarchy = (nodes) => {
    if (!Array.isArray(nodes)) return [];
    return nodes
      .filter(node => node.active === true)
      .map(node => ({
        ...node,
        children: filterActiveHierarchy(node.children || []),
      }));
};


// --- API & Auth Logic ---
const getAuthToken = () => localStorage.getItem('zTaskAuthToken');

const getApiHeaders = (isMultipart = false) => {
    const token = getAuthToken();
    if (!token) throw new Error("Authentication token not found.");
    const headers = { 'Authorization': `Bearer ${token}` };
    if (!isMultipart) {
        headers['Content-Type'] = 'application/json';
        headers['Accept'] = 'application/json';
    }
    return headers;
};

// --- API Functions ---
const getCompanyAPI = async () => {
    const response = await fetch(`${SWAGGER_BASE_URL}/api/get-company`, { headers: getApiHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch company details: ${response.statusText}`);
    return await response.json();
};

const getUserHierarchyAPI = async () => {
    const response = await fetch(`${SWAGGER_BASE_URL}/api-get-full-hierarchy-tree`, { headers: getApiHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch user hierarchy: ${response.statusText}`);
    return await response.json();
};

const getProjectsModuleBooleanAPI = async () => {
    const response = await fetch(`${NODE_BASE_URL}/api/get-column/common_master/module_boolean?module_name=projects`, { headers: getApiHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch module boolean: ${response.statusText}`);
    const data = await response.json();
    return data?.[0]?.value === true;
};

const getProjectsAPI = async () => {
    const response = await fetch(`${SWAGGER_BASE_URL}/api/get-project`, { headers: getApiHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch projects: ${response.status}`);
    return await response.json();
};

const getUsersAPI = async () => {
    const response = await fetch(`${SWAGGER_BASE_URL}/api/get-users`, { headers: getApiHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch users: ${response.statusText}`);
    return await response.json();
};

const getCustomersAPI = async () => {
    const response = await fetch(`${NODE_BASE_URL}/customer/api/customers/`, { headers: getApiHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch customers: ${response.statusText}`);
    return await response.json();
};

const getStatesAPI = async () => {
    const response = await fetch(`${NODE_BASE_URL}/api/get-column/state_master/state_name`, { headers: getApiHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch states: ${response.statusText}`);
    return await response.json();
};

const getCurrenciesAPI = async () => {
    const response = await fetch(`${NODE_BASE_URL}/api/get-column/common_master/currency`, { headers: getApiHeaders() });
    if (!response.ok) throw new Error(`Failed to fetch currencies: ${response.statusText}`);
    return await response.json();
};

const createProjectAPI = async (projectData) => {
    const response = await fetch(`${SWAGGER_BASE_URL}/api/create-project`, { method: 'POST', headers: getApiHeaders(), body: JSON.stringify(projectData) });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Project creation failed');
    }
    return await response.json();
};

const updateProjectAPI = async (projectId, projectData) => {
    const response = await fetch(`${SWAGGER_BASE_URL}/api/update-project-super-admin?project_id=${projectId}`, { method: 'PATCH', headers: getApiHeaders(), body: JSON.stringify(projectData) });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to update project');
    }
    return await response.json();
};

const uploadAttachmentsAPI = async (id, module_name, files, descriptions) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    descriptions.forEach(desc => formData.append('descriptions[]', desc || ''));

    const response = await fetch(`${ATTACHMENT_BASE_URL}/attachment/api/attachments/${id}/${module_name}`, { method: 'POST', headers: getApiHeaders(true), body: formData });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to upload attachments');
    }
    return await response.json();
};

const getAttachmentsAPI = async (row_id, module_name) => {
    const response = await fetch(`${ATTACHMENT_BASE_URL}/attachment/api/attachments/${row_id}/${module_name}`, { headers: getApiHeaders() });
    if (!response.ok) {
        if (response.status === 404) return [];
        throw new Error(`Failed to fetch attachments: ${response.statusText}`);
    }
    return await response.json();
};

const updateAttachmentAPI = async (attachmentId, payload) => {
    const response = await fetch(`${ATTACHMENT_BASE_URL}/attachment/api/attachments/${attachmentId}`, {
        method: 'PUT',
        headers: getApiHeaders(),
        body: JSON.stringify(payload),
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to update attachment');
    }
    return await response.json();
};

const getProjectCostAPI = async (projectId) => {
    const response = await fetch(`${SWAGGER_BASE_URL}/api/get-project-cost-by-project-id?projectId=${projectId}`, { headers: getApiHeaders() });
    if (!response.ok) {
        console.error(`Could not fetch project cost for ID ${projectId}, status: ${response.status}`);
        return null; 
    }
    return await response.json();
};

const deleteAttachmentAPI = async (attachmentId) => {
    const response = await fetch(`${ATTACHMENT_BASE_URL}/attachment/api/attachments/${attachmentId}`, {
        method: 'DELETE',
        headers: getApiHeaders(),
    });
    if (!response.ok && response.status !== 204) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to delete attachment');
    }
    return true;
};

const getUserByIdAPI = async (userId) => {
    const response = await fetch(`${SWAGGER_BASE_URL}/api/get-user-by-userId?userId=${userId}`, { headers: getApiHeaders() });
    if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`Failed to fetch user details: ${response.statusText}`);
    }
    return await response.json();
};

const getUserCostAPI = async (userId) => {
    const userData = await getUserByIdAPI(userId);
    if (userData && userData.user) {
        return { ratePerDay: userData.user.perDayRate, currency: userData.user.currency };
    }
    return null;
};

const setUserCostAPI = async (userId, ratePerDay, currency) => {
    const response = await fetch(`${SWAGGER_BASE_URL}/api/cost-of-user?userId=${userId}&ratePerDay=${ratePerDay}&currency=${currency}`, {
        method: 'POST',
        headers: getApiHeaders(),
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to set user cost');
    }
    return await response.json();
};


// --- Helper Hook for Outside Click ---
const useOutsideAlerter = (ref, callback) => {
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                callback();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref, callback]);
};

// --- Components ---
const PlusIcon = () => <IoAdd className="icon" aria-hidden="true" />;
const EditIcon = () => <IoPencil aria-hidden="true" />;
const DetailsIcon = () => <IoInformationCircleOutline aria-hidden="true" />;
const CloseIcon = () => <IoClose aria-hidden="true" />;
const SearchIcon = () => <IoSearch className="search-icon" aria-hidden="true" />;
const GridIcon = () => <IoGrid aria-hidden="true" />;
const ListIcon = () => <IoList aria-hidden="true" />;
const AttachIcon = () => <IoAttach aria-hidden="true" />;
const SaveIcon = () => <IoSaveOutline aria-hidden="true" />;


// --- Advanced Custom DateTime Picker Component ---
const CustomDateTimePicker = ({ value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [date, setDate] = useState(() => value ? new Date(value) : new Date());
    const popoverRef = useRef(null);
    useOutsideAlerter(popoverRef, () => setIsOpen(false));

    const selectedDayRef = useRef(null);
    const selectedMonthRef = useRef(null);
    const selectedYearRef = useRef(null);
    const selectedHourRef = useRef(null);
    const selectedMinuteRef = useRef(null);

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);
    const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const minutes = Array.from({ length: 60 }, (_, i) => i);

    useEffect(() => {
        if (value && new Date(value).getTime() !== date.getTime()) {
            setDate(new Date(value));
        }
    }, [value, date]);

    useEffect(() => {
        if (isOpen) {
            const scrollToCenter = (ref) => {
                ref.current?.scrollIntoView({ block: 'center' });
            };
            scrollToCenter(selectedDayRef);
            scrollToCenter(selectedMonthRef);
            scrollToCenter(selectedYearRef);
            scrollToCenter(selectedHourRef);
            scrollToCenter(selectedMinuteRef);
        }
    }, [isOpen]);
    
    const handleSelect = (part, val) => {
        const newDate = new Date(date);
        switch(part) {
            case 'day': newDate.setDate(val); break;
            case 'month': newDate.setMonth(val); break;
            case 'year': newDate.setFullYear(val); break;
            case 'hour': newDate.setHours(val); break;
            case 'minute': newDate.setMinutes(val); break;
            default: break;
        }
        setDate(newDate);
        onChange(newDate.toISOString());
    };

    const Column = ({ options, onSelect, selectedValue, part, refMap }) => (
        <div className="dtp-column">
            {options.map((option, index) => {
                const valueToCompare = part === 'month' ? index : option;
                const isSelected = selectedValue === valueToCompare;
                const displayText = part === 'month' ? option : String(option).padStart(2, '0');
                
                return (
                    <div 
                        key={`${part}-${option}`}
                        ref={isSelected ? refMap : null}
                        className={`dtp-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => onSelect(part, valueToCompare)}
                    >
                        {displayText}
                    </div>
                );
            })}
        </div>
    );
    
    return (
        <div className="dtp-container" ref={popoverRef}>
            <div className={`dtp-display ${isOpen ? 'focused' : ''}`} onClick={() => setIsOpen(!isOpen)}>
                {formatDateTime(date.toISOString())}
            </div>
            {isOpen && (
                <div className="dtp-popover">
                    <div className="dtp-columns-container">
                        <Column options={days} onSelect={handleSelect} selectedValue={date.getDate()} part="day" refMap={selectedDayRef} />
                        <Column options={monthNames} onSelect={handleSelect} selectedValue={date.getMonth()} part="month" refMap={selectedMonthRef} />
                        <Column options={years} onSelect={handleSelect} selectedValue={date.getFullYear()} part="year" refMap={selectedYearRef} />
                        <div className="dtp-popover-separator" />
                        <Column options={hours} onSelect={handleSelect} selectedValue={date.getHours()} part="hour" refMap={selectedHourRef} />
                        <Column options={minutes} onSelect={handleSelect} selectedValue={date.getMinutes()} part="minute" refMap={selectedMinuteRef} />
                    </div>
                </div>
            )}
        </div>
    );
};

const MultiSelectDropdown = ({ options, selected, onChange, placeholder = "Select users..." }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef(null);
    const selectAllCheckboxRef = useRef(null);
    useOutsideAlerter(wrapperRef, () => setIsOpen(false));

    const handleSelect = (optionId) => {
        const newSelected = selected.includes(optionId)
            ? selected.filter(id => id !== optionId)
            : [...selected, optionId];
        onChange(newSelected);
    };

    const displayLabel = selected.length > 0 ? `${selected.length} user(s) selected` : placeholder;

    const filteredOptions = useMemo(() => 
        options.filter(option => 
            option.name.toLowerCase().includes(searchTerm.toLowerCase())
        ), [options, searchTerm]);

    const filteredOptionIds = useMemo(() => filteredOptions.map(o => o.id), [filteredOptions]);
    const selectedInFilter = useMemo(() => 
        new Set(selected.filter(id => filteredOptionIds.includes(id))), 
        [selected, filteredOptionIds]
    );
    
    const areAllFilteredSelected = filteredOptions.length > 0 && selectedInFilter.size === filteredOptions.length;
    const areSomeFilteredSelected = selectedInFilter.size > 0 && !areAllFilteredSelected;

    useEffect(() => {
        if (selectAllCheckboxRef.current) {
            selectAllCheckboxRef.current.indeterminate = areSomeFilteredSelected;
        }
    }, [areSomeFilteredSelected]);
    
    const handleToggleAllFiltered = () => {
        const currentSelected = new Set(selected);
        if (areAllFilteredSelected) {
            filteredOptionIds.forEach(id => currentSelected.delete(id));
        } else {
            filteredOptionIds.forEach(id => currentSelected.add(id));
        }
        onChange(Array.from(currentSelected));
    };

    return (
        <div className="multi-select-container" ref={wrapperRef}>
            <div className={`multi-select-display ${isOpen ? 'open' : ''}`} onClick={() => setIsOpen(!isOpen)}>
                <span>{displayLabel}</span>
                <IoChevronDown className={`chevron-icon ${isOpen ? 'open' : ''}`} />
            </div>
            {isOpen && (
                <div className="multi-select-dropdown">
                    <div className="multi-select-search-container">
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="multi-select-search-input"
                            autoFocus
                        />
                        <IoSearch className="search-icon" />
                    </div>
                    <ul>
                        {filteredOptions.length > 0 && (
                            <li className="multi-select-option select-all-option" onClick={handleToggleAllFiltered}>
                                <input
                                    type="checkbox"
                                    ref={selectAllCheckboxRef}
                                    checked={areAllFilteredSelected}
                                    readOnly
                                />
                                <label>Select All</label>
                            </li>
                        )}
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(option => (
                                <li key={option.id} className="multi-select-option" onClick={() => handleSelect(option.id)}>
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(option.id)}
                                        readOnly
                                    />
                                    <label>{option.name}</label>
                                </li>
                            ))
                        ) : (
                            !searchTerm && options.length === 0 ? null : <li className="multi-select-no-results">No users found</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

const SearchableDropdown = ({ options, selectedId, onChange, placeholder = "Select..." }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef(null);
    useOutsideAlerter(wrapperRef, () => {
        setIsOpen(false);
        setSearchTerm('');
    });

    const handleSelect = (optionId) => {
        onChange(optionId);
        setIsOpen(false);
        setSearchTerm('');
    };

    const displayLabel = useMemo(() => {
        const selectedOption = options.find(option => String(option.id) === String(selectedId));
        return selectedOption ? selectedOption.name : placeholder;
    }, [options, selectedId, placeholder]);

    const filteredOptions = useMemo(() => 
        options.filter(option => 
            option.name.toLowerCase().includes(searchTerm.toLowerCase())
        ), [options, searchTerm]);

    return (
        <div className="searchable-dropdown-container" ref={wrapperRef}>
            <div className={`searchable-dropdown-display ${isOpen ? 'open' : ''}`} onClick={() => setIsOpen(!isOpen)}>
                <span>{displayLabel}</span>
                <IoChevronDown className={`chevron-icon ${isOpen ? 'open' : ''}`} />
            </div>
            {isOpen && (
                <div className="searchable-dropdown-menu">
                    <div className="searchable-dropdown-search-container">
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="searchable-dropdown-search-input"
                            autoFocus
                        />
                        <IoSearch className="search-icon" />
                    </div>
                    <ul>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(option => (
                                <li 
                                    key={option.id} 
                                    className={`searchable-dropdown-option ${String(option.id) === String(selectedId) ? 'selected' : ''}`} 
                                    onClick={() => handleSelect(option.id)}
                                >
                                    {option.name}
                                </li>
                            ))
                        ) : (
                            <li className="searchable-dropdown-no-results">No results found</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};


const ProjectCard = ({ project, currentUser, handleOpenDetailsModal, handleOpenEditModal, companyKeyword, subordinateIds }) => {
    const isOwner = project.createdBy && String(project.createdBy) === String(currentUser.id);
    const isReportingManager = subordinateIds.includes(project.createdBy);
    const canEdit = currentUser.role === 'SUPER_ADMIN' || isOwner || isReportingManager;

    return (
        <div className="project-card">
            <div className="project-card-content">
                <div className="project-card-main-info">
                    <h3 className="project-title">{project.projectName}</h3>
                    <p className="project-customer">{project.customerName || 'N/A'}</p>
                    {companyKeyword === 'ZO' ? (
                        <p className="project-location">
                            {formatDateTime(project.startDate, 'date')} - {formatDateTime(project.endDate, 'date')}
                        </p>
                    ) : (
                        <p className="project-location">{project.city}, {project.state || 'N/A'}</p>
                    )}
                </div>
                <div className="project-creator-info">
                    <p className="project-meta-line">
                        Created: {formatDateTime(project.createdAt)} by {project.creatorName || 'Unknown'}
                    </p>
                    {project.modifiedBy && (
                        <p className="project-meta-line">
                            Modified: {formatDateTime(project.updatedAt)} by {project.modifierName}
                        </p>
                    )}
                </div>
            </div>
            <div className="project-card-actions">
                <button className="card-action-button" onClick={() => handleOpenDetailsModal(project)}><DetailsIcon /> Details</button>
                {canEdit && <button className="card-action-button" onClick={() => handleOpenEditModal(project)}><EditIcon /> Edit</button>}
            </div>
        </div>
    );
};

const ProjectFormPage = ({ mode, formData, handleInputChange, handleSubmit, handleClose, isSubmitting, apiErrorMessage, validationErrors, states, customers, newAttachments, setNewAttachments, companyKeyword, assignableUsers, currentUser, currencies }) => {
    const title = mode === 'create' ? 'Create New Project' : 'Edit Project';
    const submitButtonText = mode === 'create' ? 'Create Project' : 'Save Changes';
    const formIdPrefix = mode === 'create' ? 'create' : 'edit';
    const [isDragging, setIsDragging] = useState(false);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
    const [newRemark, setNewRemark] = useState('');

    const [userCosts, setUserCosts] = useState({});
    const [isLoadingCosts, setIsLoadingCosts] = useState(false);
    const [editingCost, setEditingCost] = useState({ userId: null, ratePerDay: '', currency: '' });

    // --- NEW: Check for roles that can manage costs ---
    const canManageCosts = useMemo(() => {
        const privilegedRoles = ['SUPER_ADMIN', 'REPORTING_MANAGER'];
        return privilegedRoles.includes(currentUser?.role);
    }, [currentUser]);

    const selectedUsers = useMemo(() => 
        (formData.assigneeId || []).map(id => assignableUsers.find(u => u.id === id)).filter(Boolean),
        [formData.assigneeId, assignableUsers]
    );

    useEffect(() => {
        const fetchCosts = async () => {
            if (companyKeyword !== 'ZO' || !formData.assigneeId || formData.assigneeId.length === 0) {
                setUserCosts({});
                return;
            }
            setIsLoadingCosts(true);
            const costs = {};
            for (const userId of formData.assigneeId) {
                try {
                    const costData = await getUserCostAPI(userId);
                    if (costData && costData.ratePerDay) {
                        costs[userId] = { ratePerDay: costData.ratePerDay, currency: costData.currency, error: null };
                    } else {
                        const defaultCurrency = currencies && currencies.length > 0 ? currencies[0].value : 'USD';
                        costs[userId] = { ratePerDay: '', currency: defaultCurrency, error: null };
                    }
                } catch (error) {
                    console.error(`Failed to fetch cost for user ${userId}:`, error);
                    costs[userId] = { error: "Failed to load cost." };
                }
            }
            setUserCosts(costs);
            setIsLoadingCosts(false);
        };
        fetchCosts();
    }, [formData.assigneeId, companyKeyword, currencies]);


    const parsedRemarks = useMemo(() => parseRemarks(formData.remarks), [formData.remarks]);

    useEffect(() => {
        const fetchAttachments = async () => {
            if (mode === 'edit' && formData.projectId) {
                setIsLoadingAttachments(true);
                try {
                    const fetched = await getAttachmentsAPI(formData.projectId, 'projects');
                    setExistingAttachments(fetched.filter(att => att.active).map(att => ({ ...att, descriptionInput: att.description || '' })));
                } catch (error) {
                    console.error("Failed to fetch existing attachments", error);
                } finally {
                    setIsLoadingAttachments(false);
                }
            }
        };
        fetchAttachments();
    }, [mode, formData.projectId]);

    const localHandleSubmit = (e) => {
        e.preventDefault();
        const finalRemarks = formatNewRemark(newRemark, currentUser.name, formData.remarks);
        handleSubmit(e, { ...formData, remarks: finalRemarks, userCosts: userCosts });
    };

    const handleFileChange = (e) => {
        if (e.target.files) {
            const filesToAdd = Array.from(e.target.files).map(file => ({ file, description: '' }));
            setNewAttachments(prev => [...prev, ...filesToAdd]);
        }
        e.target.value = null;
    };

    const handleRemoveNewAttachment = (indexToRemove) => {
        setNewAttachments(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleNewDescriptionChange = (index, value) => {
        setNewAttachments(prev => prev.map((item, i) => (i === index ? { ...item, description: value } : item)));
    };

    const handleDragDropEvents = (e, action) => {
        e.preventDefault();
        e.stopPropagation();
        if (action === 'enter') setIsDragging(true);
        if (action === 'leave' || action === 'drop') {
            setIsDragging(false);
            if (action === 'drop' && e.dataTransfer.files?.length) {
                const filesToAdd = Array.from(e.dataTransfer.files).map(file => ({ file, description: '' }));
                setNewAttachments(prev => [...prev, ...filesToAdd]);
            }
        }
    };

    const handleExistingDescriptionChange = (attachmentId, value) => {
        setExistingAttachments(prev =>
            prev.map(att => (att.id === attachmentId ? { ...att, descriptionInput: value } : att))
        );
    };

    const handleUpdateAttachmentDescription = async (attachmentId, newDescription) => {
        try {
            await updateAttachmentAPI(attachmentId, { active: true, description: newDescription });
            setExistingAttachments(prev =>
                prev.map(att => (att.id === attachmentId ? { ...att, description: newDescription, descriptionInput: newDescription } : att))
            );
        } catch (error) {
            console.error("Failed to update description", error);
        }
    };

    const handleSoftDeleteAttachment = async (attachmentId) => {
        if (window.confirm("Are you sure you want to remove this attachment?")) {
            try {
                await deleteAttachmentAPI(attachmentId);
                setExistingAttachments(prev => prev.filter(att => att.id !== attachmentId));
            } catch (error) {
                console.error("Failed to delete attachment", error);
            }
        }
    };
    
    const handleEditCost = (userId) => {
        const currentUserCost = userCosts[userId] || { ratePerDay: '', currency: '' };
        const defaultCurrency = currencies && currencies.length > 0 ? currencies[0].value : 'USD';
        setEditingCost({
            userId: userId,
            ratePerDay: currentUserCost.ratePerDay,
            currency: currentUserCost.currency || defaultCurrency
        });
    };
    
    const handleCostInputChange = (field, value) => {
        setEditingCost(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveCost = async (userId) => {
        const { ratePerDay, currency } = editingCost;
        if (!ratePerDay || isNaN(ratePerDay) || !currency) {
            alert("Please enter a valid rate and currency.");
            return;
        }

        try {
            await setUserCostAPI(userId, ratePerDay, currency);
            setUserCosts(prev => ({
                ...prev,
                [userId]: { ratePerDay, currency, error: null }
            }));
            setEditingCost({ userId: null, ratePerDay: '', currency: '' });
        } catch (error) {
            console.error("Failed to save user cost:", error);
            alert(`Error saving cost: ${error.message}`);
        }
    };


    return (
        <div className="project-editor-page">
            <header className="editor-page-header">
                <h2>{title}</h2>
                <button className="editor-close-button" onClick={handleClose} aria-label="Close"><CloseIcon /></button>
            </header>
            <main className="editor-content-wrapper">
                <div className="editor-content">
                    <form onSubmit={localHandleSubmit} noValidate>
                        <div className="form-content">
                            {apiErrorMessage && <div className="error-message">{apiErrorMessage}</div>}

                            <div className={`form-group ${validationErrors.projectName ? 'invalid' : ''}`}>
                                <label htmlFor={`${formIdPrefix}ProjectName`}>Project Title<span className="required-mark">*</span></label>
                                <input type="text" id={`${formIdPrefix}ProjectName`} name="projectName" value={formData.projectName || ''} onChange={(e) => handleInputChange('projectName', e.target.value)} placeholder="Enter the project title" required />
                                {validationErrors.projectName && <span className="validation-error-message">{validationErrors.projectName}</span>}
                            </div>

                            {companyKeyword === 'ZO' ? (
                                <>
                                    <div className="form-row">
                                        <div className={`form-group ${validationErrors.startDate ? 'invalid' : ''}`}>
                                            <label>Start Date<span className="required-mark">*</span></label>
                                            <CustomDateTimePicker value={formData.startDate} onChange={(val) => handleInputChange('startDate', val)} />
                                            {validationErrors.startDate && <span className="validation-error-message">{validationErrors.startDate}</span>}
                                        </div>
                                        <div className={`form-group ${validationErrors.endDate ? 'invalid' : ''}`}>
                                            <label>End Date<span className="required-mark">*</span></label>
                                            <CustomDateTimePicker value={formData.endDate} onChange={(val) => handleInputChange('endDate', val)} />
                                            {validationErrors.endDate && <span className="validation-error-message">{validationErrors.endDate}</span>}
                                        </div>
                                    </div>
                                    <div className={`form-group ${validationErrors.assigneeId ? 'invalid' : ''}`}>
                                        <label>Assignees<span className="required-mark">*</span></label>
                                        <MultiSelectDropdown
                                            options={assignableUsers}
                                            selected={formData.assigneeId || []}
                                            onChange={(selectedIds) => handleInputChange('assigneeId', selectedIds)}
                                        />
                                        {validationErrors.assigneeId && <span className="validation-error-message">{validationErrors.assigneeId}</span>}
                                    </div>
                                     {selectedUsers.length > 0 && (
                                        <div className="user-cost-management-section">
                                            <h3 className="attachment-section-title">Assignee Costs</h3>
                                            {isLoadingCosts ? <p>Loading user costs...</p> : (
                                                <div className="user-cost-list">
                                                    {selectedUsers.map(user => (
                                                        <div key={user.id} className="user-cost-item">
                                                            <span className="user-cost-name">{user.name}</span>
                                                            
                                                            {/* --- MODIFIED: Conditional rendering based on role --- */}
                                                            {canManageCosts && editingCost.userId === user.id ? (
                                                                <div className="user-cost-edit-form">
                                                                    <input type="number" value={editingCost.ratePerDay} onChange={(e) => handleCostInputChange('ratePerDay', e.target.value)} placeholder="Rate per Day" className="user-cost-input" />
                                                                    <select value={editingCost.currency} onChange={(e) => handleCostInputChange('currency', e.target.value)} className="user-cost-input" required >
                                                                        <option value="" disabled>Select...</option>
                                                                        {currencies && currencies.map(c => (<option key={c.id} value={c.value}>{c.value}</option>))}
                                                                    </select>
                                                                    <button type="button" className="button-save-cost" onClick={() => handleSaveCost(user.id)}><SaveIcon /></button>
                                                                    <button type="button" className="button-cancel-cost" onClick={() => setEditingCost({ userId: null })}><CloseIcon /></button>
                                                                </div>
                                                            ) : (
                                                                <div className="user-cost-display">
                                                                    {userCosts[user.id]?.error ? ( <span className="cost-error">{userCosts[user.id].error}</span>
                                                                    ) : userCosts[user.id]?.ratePerDay ? ( <span className="cost-value">{userCosts[user.id].ratePerDay} {userCosts[user.id].currency} / day</span>
                                                                    ) : ( <span className="cost-not-set">No cost set</span> )}
                                                                    
                                                                    {/* Edit button only for authorized roles */}
                                                                    {canManageCosts && (
                                                                        <button type="button" className="button-edit-cost" onClick={() => handleEditCost(user.id)}><EditIcon /></button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                </>
                            ) : (
                                <>
                                    <div className={`form-group ${validationErrors.address ? 'invalid' : ''}`}>
                                        <label htmlFor={`${formIdPrefix}Address`}>Address<span className="required-mark">*</span></label>
                                        <textarea id={`${formIdPrefix}Address`} name="address" value={formData.address || ''} onChange={(e) => handleInputChange('address', e.target.value)} rows="3" placeholder="Enter the full street address" required></textarea>
                                        {validationErrors.address && <span className="validation-error-message">{validationErrors.address}</span>}
                                    </div>
                                    <div className="form-row">
                                        <div className={`form-group ${validationErrors.city ? 'invalid' : ''}`}>
                                            <label htmlFor={`${formIdPrefix}City`}>City<span className="required-mark">*</span></label>
                                            <input type="text" id={`${formIdPrefix}City`} name="city" value={formData.city || ''} onChange={(e) => handleInputChange('city', e.target.value)} placeholder="Enter city name" required />
                                            {validationErrors.city && <span className="validation-error-message">{validationErrors.city}</span>}
                                        </div>
                                        <div className={`form-group ${validationErrors.pinCode ? 'invalid' : ''}`}>
                                            <label htmlFor={`${formIdPrefix}PinCode`}>Pin Code<span className="required-mark">*</span></label>
                                            <input type="tel" id={`${formIdPrefix}PinCode`} name="pinCode" value={formData.pinCode || ''} onChange={(e) => handleInputChange('pinCode', e.target.value)} placeholder="e.g., 400001" required minLength="6" maxLength="6" pattern="\d{6}" title="Pin Code must be exactly 6 digits." />
                                            {validationErrors.pinCode && <span className="validation-error-message">{validationErrors.pinCode}</span>}
                                        </div>
                                    </div>
                                    <div className={`form-group ${validationErrors.stateId ? 'invalid' : ''}`}>
                                        <label>State<span className="required-mark">*</span></label>
                                        <SearchableDropdown
                                            options={states.map(state => ({ id: state.id, name: state.value }))}
                                            selectedId={formData.stateId}
                                            onChange={(id) => handleInputChange('stateId', id)}
                                            placeholder="Select a state"
                                        />
                                        {validationErrors.stateId && <span className="validation-error-message">{validationErrors.stateId}</span>}
                                    </div>
                                </>
                            )}

                            <div className="form-group">
                                <label>Customer (Optional)</label>
                                <SearchableDropdown
                                    options={customers.map(cust => ({ id: cust.id, name: cust.name }))}
                                    selectedId={formData.customerId}
                                    onChange={(id) => handleInputChange('customerId', id)}
                                    placeholder="None"
                                />
                            </div>
                            
                            <div className="form-group">
                                {mode === 'create' ? (
                                     <>
                                        <label htmlFor={`${formIdPrefix}NewRemark`}>Remarks (Optional)</label>
                                        <textarea id={`${formIdPrefix}NewRemark`} value={newRemark} onChange={(e) => setNewRemark(e.target.value)} rows="4" placeholder="Add any relevant notes or remarks..."></textarea>
                                     </>
                                ) : (
                                    <div className="remarks-section">
                                        <label htmlFor={`${formIdPrefix}NewRemark`} className="new-remark-label">Add New Remark</label>
                                        <textarea id={`${formIdPrefix}NewRemark`} value={newRemark} onChange={(e) => setNewRemark(e.target.value)} rows="3" placeholder="Type a new remark to add upon saving..."></textarea>
                                        
                                        <label>Remark History</label>
                                        {parsedRemarks.length > 0 ? (
                                            <div className="remarks-history">
                                                {parsedRemarks.map((remark, index) => (
                                                    <div key={index} className="remark-item">
                                                        <p className="remark-text">{remark.text}</p>
                                                        <span className="remark-meta">by <strong>{remark.user}</strong> on {remark.timestamp}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : <p className="no-remarks-message">No remarks yet.</p>}
                                    </div>
                                )}
                            </div>


                            <div className="attachment-section">
                                <h3 className="attachment-section-title">{mode === 'edit' ? 'Add New Attachments' : 'Attachments'}</h3>
                                 <label 
                                    htmlFor="file-upload"
                                    className={`drop-zone ${isDragging ? 'drag-over' : ''}`}
                                    onDrop={(e) => handleDragDropEvents(e, 'drop')}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDragEnter={(e) => handleDragDropEvents(e, 'enter')}
                                    onDragLeave={(e) => handleDragDropEvents(e, 'leave')}
                                >
                                    <input id="file-upload" type="file" multiple onChange={handleFileChange} className="file-input-hidden" disabled={isSubmitting} />
                                    <div className="drop-zone-content">
                                        <IoCloudUploadOutline />
                                        <span>{isDragging ? 'Drop files here' : 'Drag & Drop or Click to Upload'}</span>
                                    </div>
                                </label>

                                {newAttachments.length > 0 && (
                                    <div className="file-description-list">
                                        <h4>New Files to Upload</h4>
                                        {newAttachments.map((item, index) => (
                                            <div key={`${item.file.name}-${index}`} className="file-description-item">
                                                <span className="file-name-label" title={item.file.name}>{item.file.name}</span>
                                                <input type="text" value={item.description} onChange={(e) => handleNewDescriptionChange(index, e.target.value)} placeholder="Enter a brief description..." className="file-description-input" />
                                                <button type="button" className="remove-file-btn" onClick={() => handleRemoveNewAttachment(index)} title="Remove file" disabled={isSubmitting}><IoTrashOutline /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {mode === 'edit' && (
                                <div className="attachment-section">
                                    <h3 className="attachment-section-title">Manage Existing Attachments</h3>
                                    {isLoadingAttachments ? <p>Loading attachments...</p> : (
                                        <div className="existing-attachments-list">
                                            {existingAttachments.length > 0 ? existingAttachments.map(att => (
                                                <div key={att.id} className="existing-attachment-item">
                                                    <div className="attachment-details-group">
                                                        <a href={att.file_url} target="_blank" rel="noopener noreferrer" className="attachment-link-static">
                                                            <AttachIcon />
                                                            <span>{decodeURIComponent(att.file_url.split('/').pop())}</span>
                                                        </a>
                                                        <input
                                                            type="text"
                                                            value={att.descriptionInput}
                                                            onChange={(e) => handleExistingDescriptionChange(att.id, e.target.value)}
                                                            placeholder="Enter description..."
                                                            className="file-description-input"
                                                        />
                                                    </div>
                                                    <div className="attachment-actions-group">
                                                        {att.descriptionInput !== (att.description || '') && (
                                                            <button
                                                                type="button"
                                                                className="button-save-description"
                                                                onClick={() => handleUpdateAttachmentDescription(att.id, att.descriptionInput)}
                                                            >
                                                                Save
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="remove-file-btn"
                                                            onClick={() => handleSoftDeleteAttachment(att.id)}
                                                            title="Remove attachment"
                                                        >
                                                            <IoTrashOutline />
                                                        </button>
                                                    </div>
                                                </div>
                                            )) : <p className="no-attachments-message">No existing attachments.</p>}
                                        </div>
                                    )}
                                </div>
                            )}

                        </div>
                        <div className="editor-actions">
                            <button type="button" className="button-cancel" onClick={handleClose}>Cancel</button>
                            <button type="submit" className="button-submit" disabled={isSubmitting}>{isSubmitting ? (mode === 'create' ? 'Creating...' : 'Saving...') : submitButtonText}</button>
                        </div>
                    </form>
                </div>
            </main>
        </div>
    );
};

const ProjectDetailsPage = ({ project, handleClose, users, companyKeyword }) => {
    const [attachments, setAttachments] = useState([]);
    const [isLoadingAttachments, setIsLoadingAttachments] = useState(true);
    const [projectCost, setProjectCost] = useState(null);
    const [isLoadingCost, setIsLoadingCost] = useState(true);

    const userMap = useMemo(() =>
        (users || []).reduce((acc, user) => {
            acc[user.id] = user.name;
            return acc;
        }, {}),
    [users]);
    
    const parsedRemarks = useMemo(() => parseRemarks(project.remarks), [project.remarks]);

    useEffect(() => {
        const fetchAttachments = async () => {
            if (!project?.projectId) return;
            setIsLoadingAttachments(true);
            try {
                const fetchedAttachments = await getAttachmentsAPI(project.projectId, 'projects');
                setAttachments(fetchedAttachments.filter(att => att.active));
            } catch (error) {
                console.error("Failed to fetch attachments:", error);
            } finally {
                setIsLoadingAttachments(false);
            }
        };
        fetchAttachments();
    }, [project]);

    useEffect(() => {
        const fetchProjectCost = async () => {
            if (companyKeyword !== 'ZO' || !project?.projectId) {
                setIsLoadingCost(false);
                return;
            }

            setIsLoadingCost(true);
            try {
                const costData = await getProjectCostAPI(project.projectId);
                if (costData && typeof costData["project cost : "] === 'string') {
                    const costString = costData["project cost : "];
                    const numericMatch = costString.match(/\d+/);
                    setProjectCost(numericMatch ? numericMatch[0] : 'Not available');
                } else {
                    setProjectCost('Not available');
                }
            } catch (error) {
                console.error("Failed to fetch project cost:", error);
                setProjectCost('Error');
            } finally {
                setIsLoadingCost(false);
            }
        };

        fetchProjectCost();
    }, [project?.projectId, companyKeyword]);

    return (
        <div className="project-editor-page">
            <header className="editor-page-header">
                <h2>Project Details</h2>
                <button className="editor-close-button" onClick={handleClose} aria-label="Close"><CloseIcon /></button>
            </header>
            <main className="editor-content-wrapper">
                <div className="editor-content">
                    <div className="details-container">
                        <div className="details-grid">
                            <div className="detail-item"><span className="detail-label">Project Title</span><p className="detail-value">{project.projectName}</p></div>
                            <div className="detail-item"><span className="detail-label">Customer</span><p className="detail-value">{project.customerName || 'N/A'}</p></div>

                            {companyKeyword === 'ZO' ? (
                                <>
                                    <div className="detail-item"><span className="detail-label">Start Date</span><p className="detail-value">{formatDateTime(project.startDate)}</p></div>
                                    <div className="detail-item"><span className="detail-label">End Date</span><p className="detail-value">{formatDateTime(project.endDate)}</p></div>
                                    <div className="detail-item">
                                        <span className="detail-label">Project Cost</span>
                                        <p className="detail-value">
                                            {isLoadingCost ? 'Loading...' : (projectCost || 'N/A')}
                                        </p>
                                    </div>
                                    <div className="detail-item full-width"><span className="detail-label">Assignees</span><p className="detail-value">{(project.assigneeName || []).join(', ') || 'N/A'}</p></div>
                                </>
                            ) : (
                                <>
                                    <div className="detail-item full-width"><span className="detail-label">Full Address</span><p className="detail-value">{project.address}</p></div>
                                    <div className="detail-item"><span className="detail-label">City</span><p className="detail-value">{project.city}</p></div>
                                    <div className="detail-item"><span className="detail-label">State</span><p className="detail-value">{project.state}</p></div>
                                    <div className="detail-item"><span className="detail-label">Pin Code</span><p className="detail-value">{project.pinCode}</p></div>
                                </>
                            )}

                            <div className="detail-item"><span className="detail-label">Created By</span><p className="detail-value">{project.creatorName || 'Unknown'}</p></div>
                            <div className="detail-item"><span className="detail-label">Created On</span><p className="detail-value">{formatDateTime(project.createdAt)}</p></div>

                            {project.modifiedBy && (
                                <>
                                    <div className="detail-item"><span className="detail-label">Last Modified By</span><p className="detail-value">{project.modifierName || 'Unknown'}</p></div>
                                    <div className="detail-item"><span className="detail-label">Last Modified On</span><p className="detail-value">{formatDateTime(project.updatedAt)}</p></div>
                                </>
                            )}
                        </div>

                        <div className="remarks-section">
                            <h3 className="attachment-section-title">Remarks</h3>
                            {parsedRemarks.length > 0 ? (
                                <div className="remarks-history">
                                    {parsedRemarks.map((remark, index) => (
                                        <div key={index} className="remark-item">
                                            <p className="remark-text">{remark.text}</p>
                                            <span className="remark-meta">by <strong>{remark.user}</strong> on {remark.timestamp}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : <p className="no-remarks-message">No remarks have been added to this project.</p>}
                        </div>

                         <div className="details-attachment-section">
                             <h3 className="attachment-section-title">Attachments</h3>
                            {isLoadingAttachments ? <p>Loading attachments...</p> : attachments.length > 0 ? (
                                <table className="attachment-table">
                                    <thead>
                                        <tr>
                                            <th>File Name</th>
                                            <th>Description</th>
                                            <th>Added By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {attachments.map((att) => (
                                            <tr key={att.id}>
                                                <td>
                                                    <a href={att.file_url} target="_blank" rel="noopener noreferrer" className="attachment-link">
                                                        <AttachIcon /> 
                                                        {decodeURIComponent(att.file_url.split('/').pop())}
                                                    </a>
                                                </td>
                                                <td>{att.description?.trim() || 'No description provided.'}</td>
                                                <td>{userMap[att.added_by] || `ID: ${att.added_by}`}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : <p className="no-attachments-message">No attachments found for this project.</p>}
                        </div>
                    </div>
                    <div className="editor-actions">
                        <button type="button" className="button-cancel" onClick={handleClose}>Close</button>
                    </div>
                </div>
            </main>
        </div>
    );
};


const Project = () => {
    const [projects, setProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [states, setStates] = useState([]);
    const [currentUser, setCurrentUser] = useState({ id: null, name: '', role: 'GUEST', permissions: [] });
    const [users, setUsers] = useState([]);
    const [viewState, setViewState] = useState('list');
    const [selectedProject, setSelectedProject] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [apiErrorMessage, setApiErrorMessage] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [isAllProjectsFeatureEnabled, setIsAllProjectsFeatureEnabled] = useState(false);
    const [companyKeyword, setCompanyKeyword] = useState(null);
    const [assignableUsers, setAssignableUsers] = useState([]);
    const [hierarchyTree, setHierarchyTree] = useState([]);
    const [currencies, setCurrencies] = useState([]);

    const [createFormData, setCreateFormData] = useState({ 
        assigneeId: [], 
        startDate: new Date().toISOString(),
        endDate: new Date(new Date().getTime() + 60 * 60 * 24 * 7 * 1000).toISOString()
    });
    const [editFormData, setEditFormData] = useState({});
    const [createAttachments, setCreateAttachments] = useState([]);
    const [editAttachments, setEditAttachments] = useState([]);

    const hasCreatePermission = useMemo(() => currentUser.role === 'SUPER_ADMIN' || currentUser.permissions.includes(PERMISSIONS.CREATE_PROJECT), [currentUser]);
    const hasViewAllPermission = useMemo(() => currentUser.role === 'SUPER_ADMIN' || currentUser.permissions.includes(PERMISSIONS.VIEW_ALL_PROJECTS), [currentUser]);

    const canViewAllProjects = useMemo(() => {
        if (currentUser.role === 'SUPER_ADMIN') return true;
        return isAllProjectsFeatureEnabled && hasViewAllPermission;
    }, [isAllProjectsFeatureEnabled, hasViewAllPermission, currentUser.role]);
    
    const subordinateIds = useMemo(() => {
        if (currentUser.role === 'SUPER_ADMIN' || !hierarchyTree.length) {
            return [];
        }
        const currentUserNode = findUserInHierarchy(hierarchyTree, currentUser.name);
        if (currentUserNode) {
            return getAllSubordinates(currentUserNode).map(s => s.id);
        }
        return [];
    }, [hierarchyTree, currentUser]);


    useEffect(() => {
        const userDetailsString = localStorage.getItem('zTaskUserDetails');
        if (userDetailsString) {
            const userDetails = JSON.parse(userDetailsString);
            const userPermissions = (userDetails.role?.permissions || []).map(p => p.title.toUpperCase());
            setCurrentUser({ id: userDetails.id, name: userDetails.name, role: userDetails.role?.name || 'GUEST', permissions: userPermissions });
        } else {
            setIsLoading(false);
        }
    }, []);

    const flattenHierarchy = (nodes) => {
        let userList = [];
        const traverse = (node) => {
            if (node.id && node.name) {
                userList.push({ id: node.id, name: node.name });
            }
            if (node.children && node.children.length > 0) {
                node.children.forEach(traverse);
            }
        };
        nodes.forEach(traverse);
        return userList.filter((user, index, self) => index === self.findIndex((u) => u.id === user.id));
    };

    const loadData = useCallback(async () => {
        if (!currentUser.id) return;

        setIsLoading(true);
        setLoadError(null);
        try {
            const [moduleBoolean, companyData, projectsData, customerData, stateData, usersData, currenciesData] = await Promise.all([
                getProjectsModuleBooleanAPI(),
                getCompanyAPI(),
                getProjectsAPI(),
                getCustomersAPI(),
                getStatesAPI(),
                getUsersAPI(),
                getCurrenciesAPI()
            ]);

            setIsAllProjectsFeatureEnabled(moduleBoolean);
            const keyword = companyData?.message?.[0]?.companyKeyword;
            setCompanyKeyword(keyword);
            setCurrencies(currenciesData || []);
            
            const activeUsers = (usersData.userData || []).filter(u => u.active === true);
            setUsers(activeUsers);

            if (keyword === 'ZO') {
                const hierarchyData = await getUserHierarchyAPI();
                const activeTree = filterActiveHierarchy(hierarchyData.tree || []);
                setHierarchyTree(activeTree);

                if (currentUser.role === 'SUPER_ADMIN') {
                    setAssignableUsers(flattenHierarchy(activeTree));
                } else {
                    const currentUserNode = findUserInHierarchy(activeTree, currentUser.name);
                    let visibleUsers = [{ id: currentUser.id, name: currentUser.name }];
                    if (currentUserNode) {
                        const subordinates = getAllSubordinates(currentUserNode);
                        visibleUsers = visibleUsers.concat(subordinates);
                    }
                    const uniqueUsers = visibleUsers.filter((user, index, self) =>
                        index === self.findIndex((u) => u.id === user.id)
                    );
                    setAssignableUsers(uniqueUsers);
                }
            }

            const stateMap = (stateData || []).reduce((acc, state) => ({ ...acc, [state.id]: state.value }), {});
            const userMap = activeUsers.reduce((acc, user) => ({ ...acc, [user.id]: user.name }), {});
            const customerMap = (customerData || []).reduce((acc, cust) => ({ ...acc, [cust.id]: cust.name }), {});
            
            const enrichedProjects = (projectsData.projects || []).map(p => ({
                ...p, 
                state: p.stateId ? stateMap[p.stateId] : 'N/A', 
                creatorName: userMap[p.createdBy] || 'Unknown', 
                modifierName: p.modifiedBy ? userMap[p.modifiedBy] : null,
                customerName: p.customerId ? customerMap[p.customerId] : 'N/A',
                assigneeName: Array.isArray(p.assigneeId) ? p.assigneeId.map(id => userMap[id]).filter(Boolean) : [],
            }));


            setProjects(enrichedProjects);
            setCustomers((customerData || []).filter(c => c.active));
            setStates(stateData || []);
        } catch (error) {
            setLoadError(error.message);
            console.error("Failed to load data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [currentUser.id, currentUser.name, currentUser.role]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const { myProjects, assignedProjects, otherProjects } = useMemo(() => {
        const my = [];
        const assigned = [];
        const others = [];
        const userId = currentUser.id;
        const lowerCaseTerm = searchTerm.toLowerCase();

        for (const project of projects) {
            const matchesSearch = !searchTerm ||
                project.projectName?.toLowerCase().includes(lowerCaseTerm) ||
                project.customerName?.toLowerCase().includes(lowerCaseTerm) ||
                project.city?.toLowerCase().includes(lowerCaseTerm);

            if (matchesSearch) {
                const isCreator = String(project.createdBy) === String(userId);
                const isAssignee = Array.isArray(project.assigneeId) && project.assigneeId.includes(userId);

                if (isCreator) {
                    my.push(project);
                } else if (isAssignee) {
                    assigned.push(project);
                } else {
                    others.push(project);
                }
            }
        }
        return { myProjects: my, assignedProjects: assigned, otherProjects: others };
    }, [projects, searchTerm, currentUser.id]);

    const validateProjectData = (data, companyKeyword) => {
        const errors = {};
    
        if (companyKeyword === 'ZO') {
            if (!data.projectName?.trim()) errors.projectName = 'Project Title is required.';
            if (!data.startDate) errors.startDate = 'Start Date is required.';
            if (!data.endDate) errors.endDate = 'End Date is required.';
            if (data.startDate && data.endDate && new Date(data.endDate) < new Date(data.startDate)) {
                errors.endDate = 'End Date cannot be before Start Date.';
            }
            if (!data.assigneeId || data.assigneeId.length === 0) {
                errors.assigneeId = 'At least one assignee is required.';
            }
        } else {
            if (!data.projectName?.trim()) errors.projectName = 'Project Title is required.';
            if (!data.address?.trim()) errors.address = 'Address is required.';
            if (!data.city?.trim()) errors.city = 'City is required.';
            if (!data.stateId) errors.stateId = 'State is required.';
            if (!data.pinCode || !/^\d{6}$/.test(String(data.pinCode).trim())) {
                errors.pinCode = 'Pin Code must be exactly 6 digits.';
            }
        }
        return errors;
    };

    const resetAllForms = () => {
        setCreateFormData({ 
            projectName: '', city: '', pinCode: '', stateId: '', customerId: '', address: '', remarks: '',
            assigneeId: [], 
            startDate: new Date().toISOString(), 
            endDate: new Date(new Date().getTime() + 60 * 60 * 24 * 7 * 1000).toISOString() 
        });
        setEditFormData({});
        setCreateAttachments([]);
        setEditAttachments([]);
        setApiErrorMessage('');
        setValidationErrors({});
    };

    const handleOpenCreatePage = () => { resetAllForms(); setSuccessMessage(''); setViewState('create'); };
    const handleOpenEditPage = (project) => {
        resetAllForms();
        const projectToEdit = { ...project };
        if (companyKeyword === 'ZO') {
            projectToEdit.startDate = project.startDate || new Date().toISOString();
            projectToEdit.endDate = project.endDate || new Date(new Date().getTime() + 60 * 60 * 24 * 7 * 1000).toISOString();
        }
        setEditFormData(projectToEdit);
        setViewState('edit');
    };
    const handleOpenDetailsPage = (project) => { setSelectedProject(project); setViewState('details'); };
    const handleClosePages = () => { setViewState('list'); resetAllForms(); };
    
    const handleInputChange = (formSetter) => (name, value) => {
        formSetter(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateProject = async (e, fullFormData) => {
        const validationErrorObj = validateProjectData(fullFormData, companyKeyword);
        if (Object.keys(validationErrorObj).length > 0) {
            setValidationErrors(validationErrorObj);
            return;
        }

        setApiErrorMessage('');
        setValidationErrors({});
        setIsSubmitting(true);
        try {
            let payload;
            if (companyKeyword === 'ZO') {
                const assigneeNames = (fullFormData.assigneeId || [])
                    .map(id => assignableUsers.find(user => user.id === id)?.name)
                    .filter(Boolean);
                
                const rates = [];
                let projectCurrency = '';
                if (fullFormData.assigneeId && fullFormData.userCosts) {
                    fullFormData.assigneeId.forEach(id => {
                        const costInfo = fullFormData.userCosts[id];
                        rates.push(costInfo?.ratePerDay ? Number(costInfo.ratePerDay) : 0);
                        if (!projectCurrency && costInfo?.currency) {
                            projectCurrency = costInfo.currency;
                        }
                    });
                }
                if (!projectCurrency) projectCurrency = currencies[0]?.value || 'INR';

                payload = {
                    ...fullFormData,
                    createdBy: String(currentUser.id),
                    active: true,
                    assigneeName: assigneeNames,
                    customerId: fullFormData.customerId || 0,
                    city: "N/A", pinCode: 0, stateId: 0, address: "N/A",
                    ratePerDay: rates,
                    currency: projectCurrency,
                };
                delete payload.userCosts;

            } else {
                payload = { ...fullFormData, createdBy: String(currentUser.id), active: true, customerId: fullFormData.customerId || 0, assigneeId: [], assigneeName: [] };
            }

            await createProjectAPI(payload);

            if (createAttachments.length > 0) {
                const allProjectsData = await getProjectsAPI();
                const sortedProjects = (allProjectsData.projects || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                
                const newProject = sortedProjects.find(p => 
                    p.projectName === payload.projectName && 
                    String(p.createdBy) === String(currentUser.id)
                );

                if (newProject && newProject.projectId) {
                    const files = createAttachments.map(a => a.file);
                    const descriptions = createAttachments.map(a => a.description);
                    await uploadAttachmentsAPI(newProject.projectId, 'projects', files, descriptions);
                    setSuccessMessage('Project and attachments created successfully!');
                } else {
                    setSuccessMessage('Project created! Could not auto-attach files. Please edit the project to add them.');
                }
            } else {
                setSuccessMessage('Project created successfully!');
            }

            await loadData();
            handleClosePages();
            setTimeout(() => setSuccessMessage(''), 5000);

        } catch (error) {
            setApiErrorMessage(`Project creation failed: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateProject = async (e, fullFormData) => {
        const validationErrorObj = validateProjectData(fullFormData, companyKeyword);
        if (Object.keys(validationErrorObj).length > 0) {
            setValidationErrors(validationErrorObj);
            return;
        }

        setApiErrorMessage('');
        setValidationErrors({});
        setIsSubmitting(true);
        try {
            const { projectId } = fullFormData;
            let payload;

            if (companyKeyword === 'ZO') {
                const assigneeNames = (fullFormData.assigneeId || [])
                    .map(id => assignableUsers.find(user => user.id === id)?.name)
                    .filter(Boolean);

                const rates = [];
                let projectCurrency = '';
                if (fullFormData.assigneeId && fullFormData.userCosts) {
                    fullFormData.assigneeId.forEach(id => {
                        const costInfo = fullFormData.userCosts[id];
                        rates.push(costInfo?.ratePerDay ? Number(costInfo.ratePerDay) : 0);
                        if (!projectCurrency && costInfo?.currency) {
                            projectCurrency = costInfo.currency;
                        }
                    });
                }
                if (!projectCurrency) projectCurrency = currencies[0]?.value || 'INR';
                
                payload = {
                    ...fullFormData,
                    modifiedBy: currentUser.id,
                    assigneeName: assigneeNames,
                    customerId: fullFormData.customerId || 0,
                    ratePerDay: rates,
                    currency: projectCurrency,
                };
                delete payload.userCosts;

            } else {
                payload = { ...fullFormData, modifiedBy: currentUser.id, customerId: fullFormData.customerId || 0 };
            }
            
            await updateProjectAPI(projectId, payload);

            if (editAttachments.length > 0) {
                const files = editAttachments.map(a => a.file);
                const descriptions = editAttachments.map(a => a.description);
                await uploadAttachmentsAPI(projectId, 'projects', files, descriptions);
            }

            setSuccessMessage('Project updated successfully!');
            await loadData();
            handleClosePages();
            setTimeout(() => setSuccessMessage(''), 4000);

        } catch (error) {
            setApiErrorMessage(`Failed to update: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderProjectList = (title, projectList, emptyMsg, searchEmptyMsg) => (
        <section className="project-section">
            <h2>{title}</h2>
            {projectList.length > 0 ? (
                <div className={`projects-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
                    {projectList.map(project => 
                        <ProjectCard 
                            key={project.projectId} 
                            project={project} 
                            currentUser={currentUser} 
                            handleOpenDetailsModal={handleOpenDetailsPage} 
                            handleOpenEditModal={handleOpenEditPage} 
                            companyKeyword={companyKeyword}
                            subordinateIds={subordinateIds}
                        />
                    )}
                </div>
            ) : <div className="no-projects-message">{searchTerm ? searchEmptyMsg : emptyMsg}</div>}
        </section>
    );

    if (viewState === 'create') {
        return <ProjectFormPage mode="create" formData={createFormData} handleInputChange={handleInputChange(setCreateFormData)} handleSubmit={handleCreateProject} handleClose={handleClosePages} isSubmitting={isSubmitting} apiErrorMessage={apiErrorMessage} validationErrors={validationErrors} states={states} customers={customers} newAttachments={createAttachments} setNewAttachments={setCreateAttachments} companyKeyword={companyKeyword} assignableUsers={assignableUsers} currentUser={currentUser} currencies={currencies} />;
    }

    if (viewState === 'edit') {
        return <ProjectFormPage mode="edit" formData={editFormData} handleInputChange={handleInputChange(setEditFormData)} handleSubmit={handleUpdateProject} handleClose={handleClosePages} isSubmitting={isSubmitting} apiErrorMessage={apiErrorMessage} validationErrors={validationErrors} states={states} customers={customers} newAttachments={editAttachments} setNewAttachments={setEditAttachments} companyKeyword={companyKeyword} assignableUsers={assignableUsers} currentUser={currentUser} currencies={currencies} />;
    }

    if (viewState === 'details' && selectedProject) {
        return <ProjectDetailsPage project={selectedProject} handleClose={handleClosePages} users={users} companyKeyword={companyKeyword} />;
    }

    return (
        <div className="projects-page">
            <header className="page-header-projects">
                <div className="header-text-content">
                    <h1>{canViewAllProjects ? 'All Company Projects' : 'My Projects'}</h1>
                    <p>View and manage {canViewAllProjects ? 'all projects across the company' : 'your created and assigned projects'}.</p>
                </div>
                <div className="header-actions">
                    <div className="search-container">
                        <input type="text" placeholder="Search projects..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        <SearchIcon />
                    </div>
                    <div className="view-toggle-buttons"><button className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} aria-label="Grid View"><GridIcon /></button><button className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} aria-label="List View"><ListIcon /></button></div>
                    {hasCreatePermission && <button className="create-project-button-header" onClick={handleOpenCreatePage}><PlusIcon /> Create New Project</button>}
                </div>
            </header>
            {successMessage && <div className="success-message">{successMessage}</div>}
            <main className="projects-content">
                {isLoading || !companyKeyword ? (
                     <div className={`projects-grid ${viewMode === 'list' ? 'list-view' : ''}`}>{Array.from({ length: 6 }).map((_, index) => <div key={index} className="project-card skeleton-card">{viewMode === 'grid' ? (<><div className="skeleton-line title-skeleton"></div><div className="skeleton-line subtitle-skeleton"></div><div className="skeleton-line creator-skeleton"></div></>) : (<><div className="skeleton-line list-title-skeleton"></div><div className="skeleton-line list-creator-skeleton"></div><div className="skeleton-line list-actions-skeleton"></div></>)}</div>)}</div>
                ) : loadError ? (
                    <div className="error-message">Error fetching data: {loadError}</div>
                ) : projects.length === 0 && !searchTerm ? (
                    <div className="no-projects-message">No projects have been created yet.</div>
                ) : (
                    <>
                        {renderProjectList("My Projects", myProjects, "You have not created any projects yet.", "No projects you created match your search.")}
                        {companyKeyword === 'ZO' && renderProjectList("Assigned to Me", assignedProjects, "No projects are currently assigned to you.", "No assigned projects match your search.")}
                        {canViewAllProjects && renderProjectList("All Other Projects", otherProjects, "There are no other projects in the system.", "No other projects match your search.")}
                    </>
                )}
            </main>
        </div>
    );
};

export default Project;